import { useCallback, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { bytesToGB } from "@sscs/utils";
import { usePercentageSlider } from "Hooks";
import { GetOrganizationUsageResponse, GroupUsage } from "Types";
import * as yup from "Utils/yup";
import { useCreateGroupUsageLimitMutation } from "../services/licensing.api";
import { GroupLimitForm, GroupLimitFormPercentageSlider } from "../types";

const generateLimitSchema = (
  percentageSliderSchema: yup.SchemaOf<GroupLimitFormPercentageSlider>,
  totalPortalCapacity = 0,
): yup.SchemaOf<GroupLimitForm> =>
  percentageSliderSchema.concat(
    yup.object({
      groups: yup
        .array(yup.string().required())
        .required()
        .min(1, "At least one group must be selected"),
      limited: yup
        .number()
        .required()
        .min(1, "At least 1GB must be limited")
        .max(
          totalPortalCapacity,
          `Monthly limit can not exceed total Portal capacity: ${totalPortalCapacity.toFixed(
            2,
          )}`,
        )
        .typeError("Please enter a valid number"),
    }),
  );

const useLimit = (
  filestreamPercentageFieldName: keyof GroupLimitFormPercentageSlider,
  projectsPercentageFieldName: keyof GroupLimitFormPercentageSlider,
  organizationUsage: GetOrganizationUsageResponse,
  groupUsage: GroupUsage | null,
) => {
  const [createGroupUsageLimit] = useCreateGroupUsageLimitMutation();
  const totalPortalCapacity = useMemo(
    () => bytesToGB(organizationUsage.total_portal_capacity_bytes),
    [organizationUsage],
  );

  const {
    schema: percentageSliderSchema,
    formDefaultValues,
    getSliderPercentages,
    getRemainingPercentageValueFromInput,
    onChangePercentage,
  } = usePercentageSlider<GroupLimitFormPercentageSlider>({
    firstPercentageName: filestreamPercentageFieldName,
    secondPercentageName: projectsPercentageFieldName,
    defaultSliderValue: groupUsage?.limit?.filestream_percentage ?? 50,
  });

  const limitSchema = useMemo(
    () => generateLimitSchema(percentageSliderSchema, totalPortalCapacity),
    [percentageSliderSchema, totalPortalCapacity],
  );
  const methods = useForm<GroupLimitForm>({
    resolver: yupResolver(limitSchema),
    defaultValues: {
      ...formDefaultValues,
      limited: groupUsage?.limit ? bytesToGB(groupUsage.limit.limit_bytes) : 0,
      groups: groupUsage ? [groupUsage.group_id] : [],
    },
  });

  const { setValue, watch } = methods;
  const groups = watch("groups");

  const filestream_percentage = watch(filestreamPercentageFieldName);
  const {
    firstPercentage: filestreamPercentage,
    secondPercentage: projectsPercentage,
  } = getSliderPercentages(filestream_percentage);

  const onPercentageSliderChange = useCallback(
    (value: number) => {
      onChangePercentage(value, setValue);
    },
    [onChangePercentage, setValue],
  );

  const onProjectsPercentageInputChange = useCallback(
    (projectsPercentageInputValue: string): void => {
      setValue(
        filestreamPercentageFieldName,
        getRemainingPercentageValueFromInput(projectsPercentageInputValue),
      );
    },
    [
      setValue,
      filestreamPercentageFieldName,
      getRemainingPercentageValueFromInput,
    ],
  );

  return {
    createGroupUsageLimit,
    methods,
    totalPortalCapacity,
    groups,
    filestreamPercentage,
    projectsPercentage,
    onPercentageSliderChange,
    onProjectsPercentageInputChange,
  };
};

export default useLimit;
